import { WalletIcon, type WalletIconHandle } from 'lucide-animated';
import { observer } from 'mobx-react-lite';
import { useReducedMotion } from 'motion/react';
import { useEffect, useRef } from 'react';

import { SidebarFooterRow } from '@/components/blocks/Layout/Sidebar/SidebarFooterRow';
import { ComposerBanner } from '@/components/blocks/Thread/ComposerBanner';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button, XStack } from '@/components/ui';
import { BuyUsageCreditsModal } from '@/features/subscription/buy-usage-credits-modal';
import { useHashRoute } from '@/hooks/useHashRoute';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { subscriptionStore } from './subscription.store';
import { SubscriptionPlan } from './subscription.types';
import { formatResetDate } from './usage-format';

export const isOutOfCreditsBlocking = (): boolean =>
  subscriptionStore.isOutOfCredits && !subscriptionStore.isLiteModeActive;

type OutOfCreditsCta =
  | { kind: 'upgrade'; label: string }
  | { kind: 'buy'; label: string; teamId?: string }
  | { kind: 'none' };

type OutOfCreditsState = {
  message: string;
  cta: OutOfCreditsCta;
};

const getOutOfCreditsState = (): OutOfCreditsState => {
  const { canUpgrade, currentPlan, isTeamMember, isTeamAdmin } =
    subscriptionStore;
  const team = subscriptionStore.state.team;

  if (isTeamMember) {
    return {
      message: isTeamAdmin
        ? "You're out of usage. Buy more usage credits to keep going."
        : "You're out of usage. Ask a team admin to add more.",
      cta:
        isTeamAdmin && team
          ? { kind: 'buy', label: 'Add usage', teamId: team.id }
          : { kind: 'none' },
    };
  }

  if (currentPlan === SubscriptionPlan.BASIC) {
    return {
      message: "You're out of usage. Your daily usage resets tomorrow.",
      cta: { kind: 'upgrade', label: 'Upgrade' },
    };
  }

  return {
    message: canUpgrade
      ? "You're out of usage. Add usage or upgrade to continue."
      : "You're out of usage. Add usage to continue.",
    cta: { kind: 'buy', label: 'Add usage' },
  };
};

export const getLiteModeMessage = (): string => {
  const { currentPlan, isTeamMember, isTeamAdmin } = subscriptionStore;

  if (isTeamMember) {
    return isTeamAdmin
      ? "You're on Lite, a smaller agent. Add usage to switch back."
      : "You're on Lite, a smaller agent. Ask a team admin to add usage.";
  }

  if (currentPlan === SubscriptionPlan.BASIC) {
    return "You're on Lite, a smaller agent. Upgrade to switch back.";
  }

  const resetsAt = subscriptionStore.state.creditLimits?.resets_at;
  return resetsAt
    ? `You're on Lite, a smaller agent, until ${formatResetDate(resetsAt)}.`
    : "You're on Lite, a smaller agent, until your usage resets.";
};

export const useOutOfCreditsCta = () => {
  const { setHash } = useHashRoute();
  const { cta } = getOutOfCreditsState();
  const run = () => {
    if (cta.kind === 'upgrade') {
      trackEvent('UPGRADE_BUTTON_CLICKED', {
        source: subscriptionStore.isOutOfCredits
          ? 'out_of_credits'
          : 'low_credits',
      });
      setHash('settings/Subscription');
    } else if (cta.kind === 'buy') {
      ModalsManager.showResolvable(
        BuyUsageCreditsModal,
        cta.teamId ? { teamId: cta.teamId } : {}
      );
    }
  };
  return { cta, run };
};

const useWalletHoverAnimation = () => {
  const walletRef = useRef<WalletIconHandle>(null);
  const shouldReduceMotion = useReducedMotion();
  return {
    walletRef,
    onMouseEnter: () => {
      if (!shouldReduceMotion) walletRef.current?.startAnimation();
    },
    onMouseLeave: () => walletRef.current?.stopAnimation(),
  };
};

const UsageComposerBanner = observer(
  ({ message, className }: { message: string; className?: string }) => {
    const { cta, run } = useOutOfCreditsCta();
    const { walletRef, onMouseEnter, onMouseLeave } = useWalletHoverAnimation();

    return (
      <ComposerBanner
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={cn('pr-2.5', className)}
      >
        <XStack className="items-center justify-between gap-3">
          <XStack className="min-w-0 items-center gap-3">
            <WalletIcon
              ref={walletRef}
              size={14}
              className="shrink-0 text-foreground-muted"
            />
            <span className="truncate text-sm text-foreground-subtle">
              {message}
            </span>
          </XStack>
          {cta.kind !== 'none' && (
            <Button size="sm" onClick={run}>
              {cta.label}
            </Button>
          )}
        </XStack>
      </ComposerBanner>
    );
  }
);

export const OutOfCreditsComposerBanner = observer(
  ({ className }: { className?: string }) => {
    const isVisible = isOutOfCreditsBlocking();

    useEffect(() => {
      if (isVisible)
        trackEvent('CREDIT_PAYWALL_VIEWED', {
          surface: 'composer_block',
          reason: 'out_of_credits',
        });
    }, [isVisible]);

    if (!isVisible) return null;

    return (
      <UsageComposerBanner
        message={getOutOfCreditsState().message}
        className={className}
      />
    );
  }
);

export const OutOfCreditsCollapsedComposer = observer(
  ({ className }: { className?: string }) => {
    const { cta, run } = useOutOfCreditsCta();
    const { walletRef, onMouseEnter, onMouseLeave } = useWalletHoverAnimation();

    useEffect(() => {
      trackEvent('CREDIT_PAYWALL_VIEWED', {
        surface: 'composer_block',
        reason: 'out_of_credits',
      });
    }, []);

    return (
      <XStack
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={cn(
          'h-full w-full items-center justify-between gap-3 px-3',
          className
        )}
      >
        <XStack className="min-w-0 items-center gap-3">
          <WalletIcon
            ref={walletRef}
            size={14}
            className="shrink-0 text-foreground-muted"
          />
          <span className="truncate text-sm text-foreground-subtle">
            You&apos;re out of usage
          </span>
        </XStack>
        {cta.kind !== 'none' && (
          <Button size="sm" onClick={run}>
            {cta.label}
          </Button>
        )}
      </XStack>
    );
  }
);

export const LiteModeBanner = observer(() => {
  const isVisible = subscriptionStore.isLiteModeActive;

  useEffect(() => {
    if (isVisible)
      trackEvent('CREDIT_PAYWALL_VIEWED', {
        surface: 'composer_banner',
        reason: 'lite_mode',
      });
  }, [isVisible]);

  if (!isVisible) return null;

  return <UsageComposerBanner message={getLiteModeMessage()} />;
});

export const UsageStatusSidebarCard = observer(() => {
  const { cta, run } = useOutOfCreditsCta();
  const { walletRef, onMouseEnter, onMouseLeave } = useWalletHoverAnimation();

  const isOut = subscriptionStore.isOutOfCredits;
  const isLow = subscriptionStore.isLowOnCredits;
  const isVisible = isOut || isLow;

  useEffect(() => {
    if (isVisible)
      trackEvent('CREDIT_PAYWALL_VIEWED', {
        surface: 'sidebar',
        reason: isOut ? 'out_of_credits' : 'low_credits',
      });
  }, [isVisible, isOut]);

  if (!isVisible) return null;

  const canAct = cta.kind !== 'none';

  return (
    <SidebarFooterRow
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        'bg-gradient-to-r',
        isOut
          ? 'from-red-500/20 to-red-500/0'
          : 'from-amber-500/20 to-amber-500/0'
      )}
      icon={
        <WalletIcon
          ref={walletRef}
          size={14}
          className={
            isOut
              ? 'text-red-600 dark:text-red-400'
              : 'text-amber-600 dark:text-amber-400'
          }
        />
      }
      label={isOut ? 'Out of usage' : 'Low on usage'}
      description={canAct ? undefined : 'Ask a team admin'}
      trailing={
        canAct ? (
          <Button size="xs" variant="outline" onClick={run}>
            {cta.kind === 'buy' ? 'Add' : cta.label}
          </Button>
        ) : undefined
      }
    />
  );
});
